import { type ReactElement, useMemo } from 'react'
import { useQueryState } from 'nuqs'
import { parseAsUserId } from '@util'
import { type RecordingData, getCreatedAt, type UserId, ALL_COMPANIES } from '@terros/common'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { Group, Loader } from '@mantine/core'
import { useToastError } from '@hooks'
import { formatDate, formatOptionalDate, useRecordings } from '@front/model'
import {
  NotReady,
  Page,
  RecordingConnectionTime,
  RecordingDuration,
  TAlert,
  TDataTable,
  UserInlineSelect,
} from '@components'

function RecordingsPage(): ReactElement {
  const [userId, setUserId] = useQueryState<UserId>('userId', parseAsUserId)
  const hasSelectedUser = userId !== null
  const {
    data: recordings,
    refetch,
    isFetching,
    isLoading,
    error,
  } = useRecordings({ userId: userId ?? ('' as UserId), enabled: hasSelectedUser })
  const navigate = useNavigate()
  useToastError(error)

  const sortedRecordings = useMemo(() => {
    return [...(recordings ?? [])].sort((a, b) => getStartedAt(b) - getStartedAt(a))
  }, [recordings])

  if (hasSelectedUser && isLoading) {
    return <NotReady data={recordings} error={error} id='Recordings' isLoading={isLoading} />
  }

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title='Recordings' />
      <Page.FunctionBar>
        <Group className='w-full' justify='end'>
          <UserInlineSelect
            allowDeselect
            label='User'
            companyId={ALL_COMPANIES}
            placeholder='Select user'
            value={userId ?? undefined}
            onChange={(value) => setUserId(value ?? null)}
          />
        </Group>
      </Page.FunctionBar>
      <Page.Content scrollArea>
        {!hasSelectedUser && <TAlert title='Select a user to view recordings' />}
        {isLoading && <Loader />}
        {hasSelectedUser && (
          <TDataTable<RecordingData>
            key='recordings'
            records={sortedRecordings}
            onRowClick={(record) => {
              navigate({ to: `/recordings/${record.recordingId}` })
            }}
            columns={[
              {
                accessor: 'recordingId',
                title: 'Recording ID',
                render: (rec) => rec.recordingId,
              },
              {
                accessor: 'startedAt',
                title: 'Started',
                render: (rec) => {
                  const startedAt = getStartedAt(rec)
                  if (!startedAt) return
                  return formatDate(startedAt)
                },
              },
              {
                accessor: 'connectedAt',
                title: 'Connected',
                render: (rec) => (
                  <RecordingConnectionTime connectedAt={rec.connectedAt} noAudioSince={rec.noAudioSince} />
                ),
              },
              {
                accessor: 'endedAt',
                title: 'Ended',
                render: (rec) => formatOptionalDate(rec.endedAt),
              },
              {
                accessor: 'duration',
                title: 'Duration',
                render: (rec) => <RecordingDuration recording={rec} />,
              },
            ]}
            idAccessor={(item) => item.recordingId}
          />
        )}
      </Page.Content>
    </Page>
  )
}

function getStartedAt(recording: RecordingData): number {
  return recording.startedAt ?? getCreatedAt(recording) ?? 0
}

export const Route = createFileRoute('/recordings/')({
  component: RecordingsPage,
})
